import React, { useEffect, useState } from "react";
import styles from './CreateHealthSystem.module.scss';
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import axiosClient from "../../../api/axiosClient";
import { useNavigate, useParams } from "react-router-dom";
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import { useToasts } from "../../../contexts/ToastContext";
import HealthSystemForm from "../../../components/healthSystemForm/HealthSystemForm";
import { Tab, TabPanel, Tabs } from "../../../components/tabs/Tabs";
import Icon from "../../../components/icon/Icon";
import { formatPhoneNumber } from "../../../utils/stringUtils";
import DocumentUploadModal from "../../../components/documentUploadModal/DocumentUploadModal";
import UpdateDocumentModal from "../../../components/updateDocumentModal/UpdateDocumentModal";
import Filter from "../../../components/filter/Filter";
import Button from "../../../components/button/Button";
import ClinicalSiteCard from "../../../components/clinicalSiteCard/ClinicalSiteCard";

const CreateHealthSystem = () => {
  const { systemId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const [isLoading, setIsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    isActive: true,
    address: '',
    email: '',
    phone: '',
    website: '',
    contactPerson: '',
    designation: '',
    contactPhone: '',
    contactEmail: ''
  });

  const [documents, setDocuments] = useState([]);
  const [documentFilter, setDocumentFilter] = useState('All Documents');
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!systemId) return;
      
      setIsDocumentsLoading(true);
      const params = {};
      
      if (documentFilter === 'Expired') {
        params.expiryStatus = 'expired';
      } else if (documentFilter === '30 Days Left') {
        params.expiryStatus = '30_days_left';
      } else if (documentFilter === '60 Days Left') {
        params.expiryStatus = '60_days_left';
      }

      try {
        const response = await axiosClient.get(`health-systems/${systemId}/documents`, { params });
        setDocuments(response.data);
      } catch (error) {
        console.error("Failed to load documents", error);
      } finally {
        setIsDocumentsLoading(false);
      }
    };
    fetchDocuments();
  }, [systemId, documentFilter, refreshTrigger]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  useEffect(() => {
    const fetchSystemDetails = async () => {
      if (!systemId) return;

      setIsLoading(true);
      try {
        const response = await axiosClient.get(`health-systems/${systemId}`);
        const data = response.data;
        setFormData({
          name: data.name || '',
          isActive: data.status ?? data.isActive ?? true,
          address: data.address || '',
          email: data.email || '',
          phone: data.mobileNumber ? formatPhoneNumber(data.mobileNumber) : '', 
          website: data.website || '',
          contactPerson: data.contactPerson || '',
          designation: data.contactPersonDesignation || '', 
          contactPhone: data.contactPersonPhone ? formatPhoneNumber(data.contactPersonPhone) : '', 
          contactEmail: data.contactPersonEmail || '' 
        });
      } catch (error) {
        showToast(error.message, 'error');
        navigate('/health-systems');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSystemDetails();
  }, [systemId, showToast, navigate]);

  const handleSave = async () => {
    setIsLoading(true);
    
    // Clean phone numbers
    const cleanPhone = formData.phone ? formData.phone.replace(/\D/g, '') : '';
    const cleanContactPhone = formData.contactPhone ? formData.contactPhone.replace(/\D/g, '') : '';

    try {
      if (systemId) {
        // Update
        const payload = {
          name: formData.name,
          primaryAddress: formData.address,
          email: formData.email,
          mobileNumber: cleanPhone,
          website: formData.website,
          contactPerson: formData.contactPerson,
          contactPersonDesignation: formData.designation,
          contactPersonPhone: cleanContactPhone,
          contactPersonEmail: formData.contactEmail
        };

        await axiosClient.patch(`health-systems/${systemId}`, payload);
        showToast('Health System updated successfully');
      } else {
        // Create
        const formDataPayload = new FormData();
        
        if (formData.name) formDataPayload.append('name', formData.name);
        if (formData.address) formDataPayload.append('primaryAddress', formData.address);
        if (formData.email) formDataPayload.append('email', formData.email);
        if (cleanPhone) formDataPayload.append('mobileNumber', cleanPhone);
        if (formData.website) formDataPayload.append('website', formData.website);
        if (formData.contactPerson) formDataPayload.append('contactPerson', formData.contactPerson);
        if (formData.designation) formDataPayload.append('contactPersonDesignation', formData.designation);
        if (cleanContactPhone) formDataPayload.append('contactPersonPhone', cleanContactPhone);
        if (formData.contactEmail) formDataPayload.append('contactPersonEmail', formData.contactEmail);

        const config = {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        };

        await axiosClient.post('health-systems/create', formDataPayload, config);
        showToast('Health System created successfully');
      }
      navigate('/health-systems');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <DocumentUploadModal 
        isOpen={isUploadModalOpen} 
        onClose={() => setIsUploadModalOpen(false)} 
        siteId={systemId}
        endpointPrefix="health-systems"
        onUploadSuccess={() => {
           setRefreshTrigger(prev => prev + 1);
        }}
      />
      <UpdateDocumentModal
        isOpen={isUpdateModalOpen}
        onClose={() => {
          setIsUpdateModalOpen(false);
          setSelectedDocument(null);
        }}
        document={selectedDocument}
        endpointPrefix="health-systems"
        onUpdateSuccess={() => {
          setRefreshTrigger(prev => prev + 1);
        }}
      />
      {(isLoading || isDocumentsLoading) && <LoadingOverlay />}
      <div className={styles.breadcrumbs}>
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Health Systems', path: '/health-systems' },
            { label: systemId ? formData.name : 'Create Health System' }
          ]}
        />
      </div>
      
      <Tabs defaultExpanded>
         <Tab>
            <Icon name="clinic-full" className={styles.tabIcon} />
               <span>About</span>
         </Tab>
         <Tab disabled={!systemId}>
            <Icon name="clipboard" className={styles.tabIcon} />
              <span>Documents</span>
         </Tab>
         <TabPanel>
          <div className={styles.container}>
            <HealthSystemForm 
              formData={formData}
              onDataChange={(updatedData) => {
                setFormData((prevData) => {
                  return {
                    ...prevData,
                    ...updatedData
                  }
                });
              }}
              onSubmit={handleSave}
              isEdit={!!systemId}
            />
          </div>
         </TabPanel>
         <TabPanel>
          <div className={styles.container}>
             <div className={styles.topBar}>
               <div className={styles.filter}>
                 <Filter
                   filters={[
                     { label: 'All Documents', value: 'All Documents' },
                     { label: 'Expired', value: 'Expired' },
                     { label: '30 Days Left', value: '30 Days Left' },
                     { label: '60 Days Left', value: '60 Days Left' }
                   ]}
                   selected={documentFilter}
                   onChange={(filter) => setDocumentFilter(filter)}
                 />
               </div>
               <div className={styles.buttonWrapper}>
                 <Button
                   label={
                     <div className={styles.uploadButtonContent}>
                       <Icon name="arrow-up" />
                       <span>Upload New Document</span>
                     </div>
                   }
                   size="medium"
                   variant="grey"
                   onClick={() => setIsUploadModalOpen(true)}
                 />
               </div>
             </div>
             
             {documents.length > 0 ? (
               <div className={styles.cardList}>
                 {documents.map((doc) => (
                   <div key={doc.id} className={styles.cardItem}>
                     <ClinicalSiteCard
                       name={doc.fileName}
                       address={`Uploaded on ${formatDate(doc.uploadedDate)}`}
                       status={doc.agreementStatus}
                       type="document"
                       detailItems={[
                         { label: 'Activation Date', value: formatDate(doc.startDate) },
                         { label: 'Expiration Date', value: formatDate(doc.endDate) },
                         { label: 'Document Type', value: doc.isAgreement ? 'Agreement' : 'Document' }
                       ]}
                        actions={[
                          {
                            label: "Download",
                            variant: "grey",
                            onClick: () => window.open(doc.fileUrl, '_blank')
                          },
                          {
                            label: "Update Document",
                            variant: "primary",
                            onClick: () => {
                              setSelectedDocument(doc);
                              setIsUpdateModalOpen(true);
                            }
                          }
                        ]}
                      />
                   </div>
                 ))}
               </div>
              ) : (
                <div className={styles.emptyState}>
                   <p>You have no documents at this time.</p>
                </div>
              )}
          </div>
         </TabPanel>
      </Tabs>
    </>
  );
};

export default CreateHealthSystem;
